/* eslint-disable */
/**
 * <CompletedHistoryPanel /> — shared between Lens & Amplify Completed tabs.
 *
 * Owns:  date-range (from/to), quick-range chips, lane/stage chips, fetch loop,
 *        sparkline header, CSV export, and the results table.
 *
 * Caller owns:  data source (fetchRows), extra filter UI/state, lane config,
 *               column shape, and the CSV filename.
 *
 * Refactored May 2026 to DRY the LensCompletedTab and ProductionPlanner
 * HistoryTab views which had ~250 lines of near-duplicate UI each.
 */
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip } from "recharts";

// ─── Date helpers ───────────────────────────────────────────────────────────
const fmt = (d) => d.toISOString().slice(0, 10);
const dayDelta = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);

// All quick-range options. Callers opt in to the subset they want via
// the `enabledRanges` prop so Lens (which doesn't have weekly cohorts)
// can hide "this_week"/"last_week" while Amplify shows them.
const QUICK_RANGE_DEFS = {
  today:      { label: "Today",         compute: (now) => ({ start: fmt(now),                                              end: fmt(now) }) },
  "7d":       { label: "Last 7 days",   compute: (now) => ({ start: fmt(new Date(now.getTime() - 7 * 86400000)),            end: fmt(now) }) },
  this_week:  { label: "This week",     compute: (now) => {
    const day = now.getDay(); const back = day === 0 ? 6 : day - 1;
    return { start: fmt(new Date(now.getFullYear(), now.getMonth(), now.getDate() - back)), end: fmt(now) };
  }},
  last_week:  { label: "Last week",     compute: (now) => {
    const day = now.getDay(); const back = day === 0 ? 6 : day - 1;
    const thisMon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - back);
    const lastMon = new Date(thisMon.getTime() - 7 * 86400000);
    const lastSun = new Date(thisMon.getTime() - 86400000);
    return { start: fmt(lastMon), end: fmt(lastSun) };
  }},
  this_month: { label: "This month",    compute: (now) => ({ start: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),    end: fmt(now) }) },
  "30d":      { label: "Last 30 days",  compute: (now) => ({ start: fmt(new Date(now.getTime() - 30 * 86400000)),           end: fmt(now) }) },
  "90d":      { label: "Last 90 days",  compute: (now) => ({ start: fmt(new Date(now.getTime() - 90 * 86400000)),           end: fmt(now) }) },
  qtd:        { label: "This quarter",  compute: (now) => {
    const m = Math.floor(now.getMonth() / 3) * 3;
    return { start: fmt(new Date(now.getFullYear(), m, 1)), end: fmt(now) };
  }},
  ytd:        { label: "Year to date",  compute: (now) => ({ start: fmt(new Date(now.getFullYear(), 0, 1)),                 end: fmt(now) }) },
};

// ─── CompletionSparkline ────────────────────────────────────────────────────
// Tiny axis-less Recharts line showing rows bucketed by day with a trend
// indicator vs the immediately-preceding window of the same length.
// Renders nothing if range is shorter than 2 days (single point sparkline
// is meaningless visual noise).
function CompletionSparkline({ rows, from, to, rowDateFor, label = "completed", testidPrefix }) {
  const data = useMemo(() => {
    const start = new Date(from);
    const end = new Date(to);
    const days = Math.max(0, dayDelta(end, start));
    if (days < 1) return [];
    // Build day buckets (inclusive of both endpoints).
    const buckets = {};
    for (let i = 0; i <= days; i++) {
      const d = new Date(start.getTime() + i * 86400000);
      buckets[fmt(d)] = 0;
    }
    (rows || []).forEach((r) => {
      const raw = rowDateFor(r);
      if (!raw) return;
      const k = String(raw).slice(0, 10);
      if (k in buckets) buckets[k] += 1;
    });
    return Object.entries(buckets).map(([day, count]) => ({ day, count }));
  }, [rows, from, to, rowDateFor]);

  // Previous-period delta — same span ending the day before `from`.
  const prevTotal = useMemo(() => {
    if (data.length < 2) return null;
    const start = new Date(from);
    const end = new Date(to);
    const span = dayDelta(end, start) + 1;
    const prevEnd = new Date(start.getTime() - 86400000);
    const prevStart = new Date(prevEnd.getTime() - (span - 1) * 86400000);
    const a = fmt(prevStart);
    const b = fmt(prevEnd);
    return (rows || []).reduce((n, r) => {
      const k = String(rowDateFor(r) || "").slice(0, 10);
      return k >= a && k <= b ? n + 1 : n;
    }, 0);
  }, [rows, from, to, rowDateFor, data.length]);

  if (data.length < 2) return null;
  const total = data.reduce((n, d) => n + d.count, 0);
  const delta = prevTotal == null ? null : total - prevTotal;
  const pct = prevTotal && prevTotal > 0 ? Math.round((delta / prevTotal) * 100) : null;
  const trendCls =
    delta == null ? "text-slate-500"
    : delta > 0   ? "text-emerald-700"
    : delta < 0   ? "text-rose-700"
    :               "text-slate-500";

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-4"
      data-testid={`${testidPrefix}-sparkline`}
    >
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
        <span className="text-2xl font-bold tabular-nums text-slate-900" data-testid={`${testidPrefix}-sparkline-total`}>
          {total}
        </span>
      </div>
      {delta != null && (
        <div className={`flex flex-col ${trendCls}`}>
          <span className="text-[10px] uppercase tracking-wide opacity-70">vs prev</span>
          <span className="text-sm font-semibold tabular-nums" data-testid={`${testidPrefix}-sparkline-delta`}>
            {delta > 0 ? "+" : ""}{delta}{pct != null ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""}
          </span>
        </div>
      )}
      <div className="flex-1 h-10 min-w-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <Line
              type="monotone"
              dataKey="count"
              stroke="#4f46e5"
              strokeWidth={1.75}
              dot={false}
              isAnimationActive={false}
            />
            <RTooltip
              cursor={{ stroke: "#94a3b8", strokeDasharray: "2 2" }}
              contentStyle={{ fontSize: 11, padding: "4px 6px", borderRadius: 4, border: "1px solid #cbd5e1" }}
              labelFormatter={(v) => v}
              formatter={(v) => [v, label]}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── CompletedHistoryPanel ──────────────────────────────────────────────────
export default function CompletedHistoryPanel({
  testidPrefix,
  fetchRows,                              // async ({from, to, lanes, signal}) => row[]
  extrasKey = "",                         // change-detect for caller-side filters (search, mine-only, voice, ...)
  renderExtraFilters,                     // optional () => JSX rendered in the filter row
  defaultRangeKey = "30d",
  enabledRanges = ["today", "7d", "30d", "90d", "qtd", "ytd"],
  lanesConfig,                            // [{id, label}]
  defaultLanes,                           // [id, ...]
  lanesLabel = "Outcome",
  countsBuilder,                          // (rows) => { total, ... }  — must produce `total`
  summaryRender,                          // (counts, from, to, loading) => JSX
  columns,                                // [{ key, label, render(row), csv(row), className? }]
  csvHeader,                              // [string, ...] — defaults to columns.map(c => c.label)
  csvFilename,                            // (from, to) => string
  csvExtraCells,                          // optional (row) => [string, ...] appended after column cells (for fields not shown in the table)
  rowId,                                  // (row) => key
  rowDateFor,                             // (row) => date string (yyyy-mm-dd or ISO)
  onRowClick,                             // (row) => void
  emptyMessage = "No items in this date range. Adjust the range or filters above.",
  sparklineLabel = "completed",
  reloadKey,                              // external trigger for refetch (e.g. parent reloads)
}) {
  const today = new Date();
  const initial = QUICK_RANGE_DEFS[defaultRangeKey]?.compute(today)
                  || QUICK_RANGE_DEFS["30d"].compute(today);
  const [from, setFrom] = useState(initial.start);
  const [to, setTo] = useState(initial.end);
  const [activeRange, setActiveRange] = useState(defaultRangeKey);
  const [lanes, setLanes] = useState(defaultLanes || (lanesConfig || []).map((l) => l.id));
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyQuickRange = (key) => {
    const def = QUICK_RANGE_DEFS[key];
    if (!def) return;
    const { start, end } = def.compute(new Date());
    setFrom(start);
    setTo(end);
    setActiveRange(key);
  };

  const toggleLane = (k) =>
    setLanes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  useEffect(() => {
    if ((lanesConfig || []).length > 0 && lanes.length === 0) { setRows([]); return; }
    const ctl = (typeof AbortController !== "undefined") ? new AbortController() : null;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await fetchRows({ from, to, lanes, signal: ctl?.signal });
        if (!cancelled) setRows(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; ctl && ctl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, lanes.join(","), extrasKey, reloadKey]);

  const counts = useMemo(
    () => (countsBuilder ? countsBuilder(rows || []) : { total: (rows || []).length }),
    [rows, countsBuilder]
  );

  const exportCsv = () => {
    const list = rows || [];
    const header = csvHeader || columns.map((c) => c.label);
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.map(escape).join(",")];
    list.forEach((r) => {
      const cells = columns.map((c) => escape(c.csv ? c.csv(r) : ""));
      if (csvExtraCells) {
        (csvExtraCells(r) || []).forEach((v) => cells.push(escape(v)));
      }
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = csvFilename(from, to);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-panel`}>
      <CompletionSparkline
        rows={rows || []}
        from={from}
        to={to}
        rowDateFor={rowDateFor}
        label={sparklineLabel}
        testidPrefix={testidPrefix}
      />

      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
        {/* Quick range chips */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-quick-ranges`}>
          <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Range</span>
          {enabledRanges.filter((k) => QUICK_RANGE_DEFS[k]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => applyQuickRange(k)}
              data-testid={`${testidPrefix}-range-${k}`}
              className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                activeRange === k
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >{QUICK_RANGE_DEFS[k].label}</button>
          ))}
          {activeRange === "custom" && (
            <span className="px-2 py-0.5 text-[10px] bg-amber-100 text-amber-800 rounded-full border border-amber-300">Custom range</span>
          )}
          <button
            type="button"
            onClick={exportCsv}
            disabled={!rows || rows.length === 0}
            data-testid={`${testidPrefix}-export-csv`}
            className="ml-auto px-2.5 py-1 text-[11px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >Export CSV</button>
        </div>

        {/* Date range + caller-supplied filters + lane/stage chips */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setActiveRange("custom"); }}
              className="h-8 px-2 text-xs rounded border border-slate-300 bg-white"
              data-testid={`${testidPrefix}-from`}
            />
          </div>
          <div className="space-y-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => { setTo(e.target.value); setActiveRange("custom"); }}
              className="h-8 px-2 text-xs rounded border border-slate-300 bg-white"
              data-testid={`${testidPrefix}-to`}
            />
          </div>

          {renderExtraFilters ? renderExtraFilters() : null}

          {(lanesConfig || []).length > 0 && (
            <div className="space-y-1 flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">{lanesLabel}</label>
              <div className="flex flex-wrap gap-1.5">
                {lanesConfig.map((s) => {
                  const on = lanes.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      aria-pressed={on}
                      onClick={() => toggleLane(s.id)}
                      className={`text-[10px] px-2 py-0.5 rounded-full border transition ${on ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                      data-testid={`${testidPrefix}-lane-${String(s.id).toLowerCase()}`}
                    >{s.label}</button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-600 ml-auto" data-testid={`${testidPrefix}-summary`}>
            {summaryRender ? summaryRender(counts, from, to, loading) : (
              loading ? "Loading…" : <>Showing <span className="font-bold">{counts.total}</span> item{counts.total === 1 ? "" : "s"}</>
            )}
          </div>
        </div>
      </div>

      {/* Results table */}
      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
        ) : !rows || rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid={`${testidPrefix}-empty`}>
            {emptyMessage}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`text-left px-3 py-2 ${c.className || ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={rowId(r)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onRowClick && onRowClick(r)}
                  data-testid={`${testidPrefix}-row-${rowId(r)}`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 ${c.className || ""}`}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Re-export Badge so call-sites can pass `<Badge/>` cells without duplicating
// import paths if they don't already import it.
export { Badge };
